import React, { useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import { Button } from "../../components/ui";
import { useHandleLogout } from "../../util";
import {
  getAuthErrorInfo,
  shouldDisplayAuthError,
} from "../../util/authErrors";
import LogoIcon from "../../assets/icons/LogoIcon";
import MicrosoftIcon from "../../assets/icons/MicrosoftIcon";
import { LOGIN } from "../../graphql/mutation/User";
import { useMutation } from "@apollo/client";

const Login = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [authError, setAuthError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const MicrosoftLogo = <MicrosoftIcon className="mr-3 h-5" />;
  const handleLogout = useHandleLogout();
  const [login] = useMutation(LOGIN);

  const handleLogin = async () => {
    setIsLoading(true);
    setAuthError(null);

    try {
      await instance.loginPopup(loginRequest);
      setTimeout(() => login(), 1000);
    } catch (error) {
      if (shouldDisplayAuthError(error)) {
        setAuthError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="grid min-h-full min-w-full grid-cols-1 lg:grid-cols-5">
        <div className="hidden flex-col items-end justify-center border-r border-gray-200 lg:col-span-3 lg:flex lg:px-[10%] lg:py-14">
          <LogoIcon className="mb-6 h-6" />
          <div className="text-lg font-medium tracking-wider text-gray-400">
            New Inventory System
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-screen-sm flex-col justify-center px-4 py-8 sm:px-6 lg:col-span-2 lg:px-14 lg:py-14">
          <div className="flex flex-col justify-center">
            {!isAuthenticated && (
              <div className="text-lg font-medium tracking-wider text-gray-900">
                Sign In
              </div>
            )}
          </div>
          <div className="mt-4">
            <div>
              <div>
                {isAuthenticated ? (
                  <p className="mb-4 text-lg font-medium tracking-wider text-gray-900">
                    Signed in as{" "}
                    <span className="text-sky-700">
                      {accounts?.[0]?.username}
                    </span>
                  </p>
                ) : null}
                <div className="mt-1 grid">
                  {isAuthenticated ? (
                    <Button
                      label="Log Out"
                      action="default"
                      onClick={() => handleLogout()}
                      icon={MicrosoftLogo}
                      className="flex flex-row items-center py-5"
                    />
                  ) : (
                    <Button
                      label={isLoading ? "Signing in..." : "Microsoft"}
                      action="default"
                      onClick={() => handleLogin()}
                      icon={MicrosoftLogo}
                      className="flex flex-row items-center py-5"
                      disabled={isLoading}
                    />
                  )}
                </div>
                {authError && !isAuthenticated && (
                  <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          {getAuthErrorInfo(authError)?.title}
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          {getAuthErrorInfo(authError)?.message}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
